// components/PaywallScreen.tsx
// Plan selection + M-Pesa payment. Used by SubscriptionGuard (hard lock)
// and TrialBanner (voluntary upgrade).

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import type { Plan } from "../context/SubscriptionContext";


const RED = "#ef4444";

const MPESA_GREEN  = "#16a34a";
const MPESA_LIGHT  = "#22c55e";
const GOLD         = "#f59e0b";
const TILL_NUMBER  = "3213248";
const WA_NUMBER    = "254783069010";

type TeamAgent = {
  id: string;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  is_active: boolean;
  plan_suspended: boolean;
  shop_id?: string | null;
};

type TeamShop = {
  id: string;
  name: string;
  active: boolean;
  plan_suspended: boolean;
};


type Step         = "plans" | "pay";
type PayTab       = "stk" | "manual";
type ManualMethod = "mpesa_manual" | "airtel" | "bank" | "other";

// ─── Yearly price helper (single source of truth) ─────────────────────────────
// Matches the formula in mpesa-stk-push, register-payment-intent,
// SubscriptionContext, and PaymentHistory. Never compute this inline again.
export function yearlyPrice(monthlyPrice: number): number {
  return Math.round(monthlyPrice * 11 / 10) * 10;
}

function buildWaHref(agentCount: number | null, shopCount: number | null): string {
  const countLine = agentCount !== null
    ? ` My business currently has ${agentCount} agent${agentCount !== 1 ? "s" : ""} and ${shopCount ?? 0} shop${(shopCount ?? 0) !== 1 ? "s" : ""}.`
    : "";
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    `Hello QASHUP Team! 👋 I'm on the app and I'd like to learn more about the Custom Plan — how it works, what features I can get, and what the pricing would be for my business.${countLine}`
  )}`;
}

// ─── Plan eligibility ────────────────────────────────────────────────────────
type Eligibility =
  | { eligible: true }
  | { eligible: false; kind: "too_many_shops" | "too_many_agents"; reason: string };

function planEligibility(
  plan: Plan,
  agentCount: number | null,
  shopCount: number | null,
  
 ): Eligibility {
  if (plan.is_custom) return { eligible: true };
  if (agentCount === null || shopCount === null) return { eligible: true };

  const shopCap  = plan.max_shops;
  const agentCap = plan.max_agents;

  const shopOver  = shopCap  !== null && shopCount  > shopCap;
  const agentOver = agentCap !== null && agentCount > agentCap;

  if (!shopOver && !agentOver) return { eligible: true };

  // Build a single reason that names everything that's over the cap
  const parts: string[] = [];
  if (shopOver) {
    parts.push(
      `${shopCount} shop${shopCount !== 1 ? "s" : ""} (max ${shopCap})`
    );
  }
  if (agentOver) {
    parts.push(
      `${agentCount} agent${agentCount !== 1 ? "s" : ""} (max ${agentCap})`
    );
  }

  return {
    eligible: false,
    kind: shopOver && !agentOver ? "too_many_shops" : "too_many_agents",
    reason:
      parts.length === 2
        ? `You have ${parts[0]} and ${parts[1]}, but ${plan.name} allows less.`
        : `You have ${parts[0]}, but ${plan.name} allows only ${
            shopOver ? shopCap : agentCap
          }.`,
  };
}

// Smallest plan (by shop+agent cap) that DOES fit the user — used for the
// "switch to X" shortcut. Returns null if none fit except Custom.
function nextFittingPlan(
  plans: Plan[],
  agentCount: number | null,
  shopCount: number | null,
): Plan | null {
  const ordered = plans
    .filter(p => !p.is_custom)
    .filter(p => planEligibility(p, agentCount, shopCount).eligible)
    .sort((a, b) => a.price - b.price);
  return ordered[0] ?? null;
}

function UsageStrip({
  agentCount, shopCount, isMobile, hasAnyBlockedPlan, onManage,
}: {
  agentCount: number | null;
  shopCount: number | null;
  isMobile: boolean;
  hasAnyBlockedPlan: boolean;
  onManage: () => void;
}) {
  const { theme, isDark } = useTheme();

  if (agentCount === null || shopCount === null) return null;

  const chip = (icon: string, count: number, label: string, color: string) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 12px", borderRadius: 8,
      background: `${color}15`, border: `1px solid ${color}40`,
      fontFamily: theme.font.mono, fontSize: 12, fontWeight: 600,
      color, whiteSpace: "nowrap",
    }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontWeight: 800 }}>{count}</span>
      <span style={{ fontWeight: 400, opacity: 0.85 }}>{label}</span>
    </span>
  );

  return (
    <div style={{
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
      border: `1px solid ${theme.border.default}`,
      borderRadius: 12,
      padding: isMobile ? "12px 14px" : "14px 16px",
      marginBottom: 16,
    }}>
      {/* ── Header row: label on the left, Manage button on the right ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 8, marginBottom: 10, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>📊</span>
          <span style={{
            fontFamily: theme.font.mono, fontSize: 10, color: theme.text.muted,
            textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600,
          }}>
            Your business
          </span>
        </div>
        <button
          onClick={onManage}
          style={{
            background: "transparent", border: "none",
            color: hasAnyBlockedPlan ? RED : theme.text.muted,
            fontFamily: theme.font.mono, fontSize: 10.5, fontWeight: 700,
            cursor: "pointer", padding: "2px 0", whiteSpace: "nowrap",
            textDecoration: "underline", textUnderlineOffset: 2,
          }}
        >
          {hasAnyBlockedPlan ? "Fix →" : "Manage team →"}
        </button>
      </div>

      {/* ── Counts ── */}
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap",
        marginBottom: hasAnyBlockedPlan ? 10 : 0,
      }}>
        {chip("👤", agentCount, agentCount === 1 ? "agent" : "agents", "#0891b2")}
        {chip("🏪", shopCount,  shopCount  === 1 ? "shop"  : "shops",  "#b45309")}
      </div>

      {/* ── Blocked CTA (only when relevant) ── */}
      {hasAnyBlockedPlan && (
        <>
          <div style={{
            fontFamily: theme.font.mono, fontSize: 11,
            color: theme.text.secondary, lineHeight: 1.6,
            paddingTop: 10, borderTop: `1px dashed ${theme.border.default}`,
          }}>
            Plans are sized to fit your team. To unlock a smaller plan,{" "}
            <strong style={{ color: theme.text.primary }}>
              suspend shops or agents
            </strong>{" "}
            — or pick a plan that fits.
          </div>
          <button
            onClick={onManage}
            style={{
              marginTop: 10, width: "100%",
              background: "rgba(239,68,68,0.10)",
              border: `1px solid ${RED}40`,
              borderRadius: 8, padding: "9px 12px",
              color: RED, fontFamily: theme.font.mono, fontSize: 11.5,
              fontWeight: 700, cursor: "pointer",
            }}
          >
            Manage agents & shops →
          </button>
        </>
      )}
    </div>
  );
}

// ─── PlanLimits (theme-aware) ────────────────────────────────────────────────
function PlanLimits({ plan }: { plan: Plan }) {
  const { theme } = useTheme();
  const agentLabel  = plan.max_agents == null ? "Unlimited" : `Up to ${plan.max_agents}`;
  const shopLabel   = plan.max_shops  == null ? "Unlimited" : `Up to ${plan.max_shops}`;
  const agentSuffix = plan.max_agents == null ? " agents"   : ` agent${plan.max_agents !== 1 ? "s" : ""}`;
  const shopSuffix  = plan.max_shops  == null ? " shops"    : ` shop${plan.max_shops  !== 1 ? "s" : ""}`;

  const chipBase: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 5,
    borderRadius: 6, padding: "3px 9px",
    fontFamily: theme.font.mono, fontSize: 11, fontWeight: 600,
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
      <span style={{ ...chipBase, background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.35)", color: "#0891b2" }}>
        👤 <span style={{ fontWeight: 800, fontSize: 12 }}>{agentLabel}</span>
        <span style={{ fontWeight: 400, color: "rgba(6,182,212,0.85)" }}>{agentSuffix}</span>
      </span>
      <span style={{ ...chipBase, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.4)", color: "#b45309" }}>
        🏪 <span style={{ fontWeight: 800, fontSize: 12 }}>{shopLabel}</span>
        <span style={{ fontWeight: 400, color: "rgba(180,83,9,0.8)" }}>{shopSuffix}</span>
      </span>
    </div>
  );
}

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

const FEATURE_LABELS: { key: string; label: string }[] = [
  { key: "scan_to_sell",  label: "Camera Scan"          },
  { key: "reports",       label: "Reports & Analytics"  },
  { key: "export",        label: "PDF & Excel Export"   },
  { key: "cash_tracker",  label: "Cash Tracker"         },
  { key: "daily_reports", label: "Daily Email Reports"  },
];


function BlockedToast({
  data, plans, onSwitch, onDismiss, onManage, isDark, theme,
}: {
  data: { planName: string; reason: string; fixPlanId: string | null };
  plans: Plan[];
  onSwitch: (planId: string) => void;
  onDismiss: () => void;
  onManage: () => void;
  isDark: boolean;
  theme: any;
}) {
  const fixPlan = data.fixPlanId ? plans.find(p => p.id === data.fixPlanId) : null;

  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        top: "max(12px, env(safe-area-inset-top, 0px))",
        left: 0, right: 0, margin: "0 auto",
        maxWidth: "min(460px, calc(100vw - 16px))",
        maxHeight: "calc(100svh - 24px)",
        overflowY: "auto",
        zIndex: 9999,
        background: isDark ? "rgba(69,10,10,0.96)" : "rgba(254,242,242,0.98)",
        border: `1px solid ${RED}66`,
        borderRadius: 12,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.55)"
          : "0 10px 30px rgba(15,23,42,0.15)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        padding: "12px 40px 12px 14px", // right padding reserves ✕ space
        animation: "toastIn 0.25s cubic-bezier(0.16,1,0.3,1) both",
        pointerEvents: "auto",
      }}
    >
      {/* ✕ dismiss — restored, absolutely positioned top-right */}
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute", top: 8, right: 8, width: 26, height: 26,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "transparent", border: "none",
          color: isDark ? "rgba(254,202,202,0.75)" : "rgba(127,29,29,0.7)",
          cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0, zIndex: 1,
        }}
      >✕</button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 16, lineHeight: 1.2, flexShrink: 0 }}>🔒</span>
        <div style={{
          flex: 1, minWidth: 0,
          fontFamily: theme.font.body, fontWeight: 700, fontSize: 12.5,
          color: RED, lineHeight: 1.35,
        }}>
          {data.planName} doesn't fit your business
        </div>
      </div>

      {/* Reason */}
      <div style={{
        fontFamily: theme.font.mono, fontSize: 11,
        color: isDark ? "#fecaca" : "#7f1d1d",
        marginTop: 4, marginLeft: 24, lineHeight: 1.5, wordBreak: "break-word",
      }}>
        {data.reason}
      </div>

      {/* Actions — stacked vertically */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 6,
        marginTop: 10, marginLeft: 24,
      }}>
        {fixPlan && (
          <button
            onClick={() => onSwitch(fixPlan.id)}
            style={{
              background: `linear-gradient(135deg,${MPESA_GREEN},#15803d)`,
              border: "none", borderRadius: 8, padding: "8px 12px",
              color: "#fff", fontFamily: theme.font.mono, fontSize: 11,
              fontWeight: 700, cursor: "pointer", textAlign: "center",
            }}
          >
            Switch to {fixPlan.name} →
          </button>
        )}
        <button
          onClick={() => { onDismiss(); onManage(); }}
          style={{
            background: "transparent", border: "none", padding: "4px 0",
            fontFamily: theme.font.mono, fontSize: 10.5,
            color: isDark ? "rgba(254,202,202,0.85)" : "rgba(127,29,29,0.85)",
            textDecoration: "underline", textUnderlineOffset: 2,
            cursor: "pointer", textAlign: "center", width: "100%",
          }}
        >
          Suspend shops / agents to fit this plan →
        </button>
      </div>
    </div>
  );
}

function TeamManagerModal({
  open, onClose,
  agentList, shopList, setAgentList, setShopList,
  loading, error, setError,
  theme, isDark, isMobile,
  onCountsChanged,
}: {
  open: boolean;
  onClose: () => void;
  agentList: TeamAgent[];
  shopList: TeamShop[];
  setAgentList: React.Dispatch<React.SetStateAction<TeamAgent[]>>;
  setShopList:  React.Dispatch<React.SetStateAction<TeamShop[]>>;
  loading: boolean;
  error: string | null;
  setError: (v: string | null) => void;
  theme: any;
  isDark: boolean;
  isMobile: boolean;
  onCountsChanged: (agents: TeamAgent[], shops: TeamShop[]) => void;
}) {
  const [tab, setTab] = useState<"agents" | "shops">("agents");
  const [savingId, setSavingId] = useState<string | null>(null);

  if (!open) return null;

  const T = {
    surface:       theme.bg.card,
    surfaceMuted:  theme.bg.base,
    border:        theme.border.default,
    text:          theme.text.primary,
    textSecondary: theme.text.secondary,
    textMuted:     theme.text.muted,
    fontBody:      theme.font.body,
    fontMono:      theme.font.mono,
    fontDisplay:   theme.font.display,
  };

  const activeAgents = agentList.filter(a => a.is_active && !a.plan_suspended).length;
  const activeShops  = shopList.filter(s => s.active && !s.plan_suspended).length;

    const toggleAgent = async (agent: TeamAgent) => {
    if (savingId) return;
    setSavingId(agent.id);
    setError(null);
    const newVal = !agent.plan_suspended;
    
    // 1. Update the profile
    const { error: err } = await supabase
      .from("profiles")
      .update({ plan_suspended: newVal })
      .eq("id", agent.id);
      
    if (err) {
      setError(err.message);
    } else {
      // 2. Mirror to shop_agents so they are blocked from selling immediately
      await supabase
        .from("shop_agents")
        .update({ active: !newVal }) // suspend -> false, reactivate -> true
        .eq("agent_id", agent.id);

      const updated = agentList.map(a =>
        a.id === agent.id ? { ...a, plan_suspended: newVal } : a,
      );
      setAgentList(updated);
      onCountsChanged(updated, shopList);
    }
    setSavingId(null);
  };

  const toggleShop = async (shop: TeamShop) => {
    if (savingId) return;
    setSavingId(shop.id);
    setError(null);
    const newVal = !shop.plan_suspended;
    const { error: err } = await supabase
      .from("shops")
      .update({ plan_suspended: newVal })
      .eq("id", shop.id);
    if (err) {
      setError(err.message);
    } else {
      const updated = shopList.map(s =>
        s.id === shop.id ? { ...s, plan_suspended: newVal } : s,
      );
      setShopList(updated);
      onCountsChanged(agentList, updated);
    }
    setSavingId(null);
  };

  const items = tab === "agents" ? agentList : shopList;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? 8 : 16,
        animation: "fadeUp 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          width: "100%", maxWidth: 500,
          maxHeight: "calc(100svh - 32px)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          color: T.text, fontFamily: T.fontBody,
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.7)"
            : "0 20px 60px rgba(15,23,42,0.25)",
        }}
      >
        {/* Header */}
          <div style={{ padding: isMobile ? "14px 16px 12px" : "18px 20px 14px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: 18 }}>
                Manage Team
              </div>
              <div style={{
                fontFamily: T.fontMono, fontSize: 11, color: T.textMuted,
                marginTop: 4, lineHeight: 1.55,
              }}>
                Suspended agents & shops <strong style={{ color: T.text }}>don't count</strong> toward your plan limit.
                Nothing is deleted — you can reactivate or delete them later from your dashboard.
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                flexShrink: 0, width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none",
                color: T.textMuted, cursor: "pointer", fontSize: 18,
              }}
            >✕</button>
          </div>
        </div>

        {/* Summary chips */}
          <div style={{ padding: isMobile ? "10px 16px 0" : "12px 20px 0", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.35)",
            borderRadius: 8, padding: "5px 12px",
            fontFamily: T.fontMono, fontSize: 11, fontWeight: 700, color: "#0891b2",
          }}>
            👤 {activeAgents} active {activeAgents === 1 ? "agent" : "agents"}
          </span>
          <span style={{
            background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.4)",
            borderRadius: 8, padding: "5px 12px",
            fontFamily: T.fontMono, fontSize: 11, fontWeight: 700, color: "#b45309",
          }}>
            🏪 {activeShops} active {activeShops === 1 ? "shop" : "shops"}
          </span>
        </div>

        {/* Tabs */}
          <div style={{ padding: isMobile ? "10px 16px 0" : "12px 20px 0", display: "flex", gap: 8 }}>
          {([
            { id: "agents" as const, label: `Agents (${agentList.length})` },
            { id: "shops"  as const, label: `Shops (${shopList.length})`  },
          ]).map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: "8px 12px", borderRadius: 10,
                border: `1.5px solid ${tab === t.id ? MPESA_GREEN : T.border}`,
                background: tab === t.id ? "rgba(22,163,74,0.10)" : T.surfaceMuted,
                color: tab === t.id ? MPESA_GREEN : T.textSecondary,
                fontFamily: T.fontMono, fontSize: 12, fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "10px 16px" : "12px 20px" }}>
          {error && (
            <div style={{
              color: "#f87171", fontSize: 11, fontFamily: T.fontMono,
              background: "rgba(248,113,113,0.08)",
              border: "1px solid rgba(248,113,113,0.2)",
              borderRadius: 8, padding: "8px 12px", marginBottom: 10,
            }}>⚠ {error}</div>
          )}
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: T.textMuted, fontFamily: T.fontMono, fontSize: 12 }}>
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: T.textMuted, fontFamily: T.fontMono, fontSize: 12 }}>
              No {tab} yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((item: any) => {
                const isSuspended = item.plan_suspended;
                const isInactive  = tab === "agents" ? !item.is_active : !item.active; 
                const counts      = !isSuspended && !isInactive;
                const busy        = savingId === item.id;
                const label = tab === "agents"
                  ? (item.full_name || item.name || item.email || "Unnamed agent")
                  : (item.name || "Unnamed shop");
                const sub = tab === "agents" ? item.email : null;

                return (
                  <div key={item.id} style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",      // ← stack on mobile
                    alignItems: isMobile ? "stretch" : "center",     // ← stretch when stacked
                    gap: isMobile ? 8 : 10,
                    background: counts ? T.surfaceMuted : "rgba(248,113,113,0.05)",
                    border: `1px solid ${counts ? T.border : "rgba(248,113,113,0.25)"}`,
                    borderRadius: 10, padding: "10px 12px",
                    opacity: isInactive ? 0.55 : 1,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div title={label} style={{
                        fontFamily: T.fontBody, fontWeight: 700, fontSize: 13,
                        color: T.text,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>{label}</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 3, flexWrap: "wrap" }}>
                        {sub && (
                          <span style={{
                            fontFamily: T.fontMono, fontSize: 10, color: T.textMuted,
                            maxWidth: 160, whiteSpace: "nowrap",
                            overflow: "hidden", textOverflow: "ellipsis",
                          }}>{sub}</span>
                        )}
                        <span style={{
                          fontFamily: T.fontMono, fontSize: 9, fontWeight: 700,
                          padding: "2px 7px", borderRadius: 5,
                          background: isSuspended ? "rgba(239,68,68,0.15)"
                                    : isInactive ? "rgba(148,163,184,0.18)"
                                    : "rgba(22,163,74,0.15)",
                          color: isSuspended ? "#ef4444"
                               : isInactive ? "#94a3b8"
                               : "#16a34a",
                        }}>
                          {isSuspended ? "SUSPENDED" : isInactive ? "INACTIVE" : "ACTIVE"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => tab === "agents" ? toggleAgent(item) : toggleShop(item)}
                      disabled={busy || isInactive}
                      style={{
                        flexShrink: 0,
                        width: isMobile ? "100%" : "auto",
                        padding: "6px 12px", borderRadius: 8,
                        border: `1px solid ${isSuspended ? MPESA_GREEN : RED}`,
                        background: isSuspended ? "rgba(22,163,74,0.10)" : "rgba(239,68,68,0.10)",
                        color: isSuspended ? MPESA_GREEN : RED,
                        fontFamily: T.fontMono, fontSize: 11, fontWeight: 700,
                        cursor: busy || isInactive ? "not-allowed" : "pointer",
                        opacity: busy || isInactive ? 0.5 : 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {busy ? "…" : isSuspended ? "Reactivate" : "Suspend"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: isMobile ? "10px 16px 14px" : "12px 20px 16px", borderTop: `1px solid ${T.border}` }}>
          <button
            onClick={onClose}
            style={{
              width: "100%",
              background: `linear-gradient(135deg,${MPESA_GREEN},#15803d)`,
              border: "none", borderRadius: 12,
              padding: "12px 0", color: "#fff",
              fontFamily: T.fontBody, fontWeight: 800, fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(22,163,74,0.35)",
            }}
          >Done</button>
        </div>
      </div>
    </div>
  );
}

export default function PaywallScreen() {
  const { theme, isDark } = useTheme();
  const width    = useWindowWidth();
  const isMobile = width < 480;

  const [blocked, setBlocked] = useState<{
  planName: string;
  reason: string;
  fixPlanId: string | null;
} | null>(null);

    // Auto-dismiss the toast after a few seconds
    useEffect(() => {
      if (!blocked) return;
      const t = setTimeout(() => setBlocked(null), 6000);
      return () => clearTimeout(t);
    }, [blocked]);

    const {
    plans, triggerStkPush, paymentPending, paymentError,
    paymentSuccess, pendingAmount, resetPaymentState,
  } = useSubscription();
    const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const [step,         setStep]         = useState<Step>("plans");
  const [selected,     setSelected]     = useState<Plan | null>(null);
  const [phone,        setPhone]        = useState("");
  const [frequency,    setFrequency]    = useState<"daily" | "weekly" | "monthly">("monthly");
  const [isYearly,     setIsYearly]     = useState(false);
  const [agentCount,   setAgentCount]   = useState<number | null>(null);
  const [shopCount,    setShopCount]    = useState<number | null>(null);

  // Manual payment state
  const [payTab,        setPayTab]       = useState<PayTab>("stk");
  const [manualRef,     setManualRef]    = useState("");
  const [manualMethod,  setManualMethod] = useState<ManualMethod>("mpesa_manual");
  const [manualPhone,   setManualPhone]  = useState("");
  // const [manualPending, setManualPending] = useState(false);
  const [manualError,   setManualError]  = useState("");
  const [manualSuccess, setManualSuccess] = useState(false);

  const resetManualForm = () => {
    setManualRef(""); setManualMethod("mpesa_manual");
    setManualPhone(""); setManualError(""); setManualSuccess(false);
  };

  const [manageOpen,    setManageOpen]    = useState(false);
  const [agentList,     setAgentList]     = useState<TeamAgent[]>([]);
  const [shopList,      setShopList]      = useState<TeamShop[]>([]);
  const [listLoading,   setListLoading]   = useState(false);
  const [listError,     setListError]     = useState<string | null>(null);

  const fetchTeam = async () => {
  if (!profile?.id) return;
  setListLoading(true);
  setListError(null);
  const [agentsRes, shopsRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("*")
      .eq("owner_id", profile.id)
      .eq("role", "agent")
      .is("deleted_at", null)
      .order("created_at", { ascending: false }),
    supabase
      .from("shops")
      .select("*")
      .eq("owner_id", profile.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false }),
  ]);
  if (agentsRes.error) setListError(agentsRes.error.message);
  else if (shopsRes.error) setListError(shopsRes.error.message);
  else {
    setAgentList(agentsRes.data ?? []);
    setShopList(shopsRes.data ?? []);
  }
  setListLoading(false);
};

const openManage = async () => {
  setManageOpen(true);
  // first open → fetch; subsequent opens reuse state
  if (agentList.length === 0 && shopList.length === 0) {
    await fetchTeam();
  }
};

const handleCountsChanged = (agents: TeamAgent[], shops: TeamShop[]) => {
  setAgentCount(agents.filter(a => a.is_active && !a.plan_suspended).length);
  setShopCount(shops.filter(s => s.active && !s.plan_suspended).length);
};

  // Effective frequency the user is actually paying for.
  const effectiveFrequency: "daily" | "weekly" | "monthly" | "yearly" =
    isYearly ? "yearly" : frequency;

  // Fetch owner's agent + shop counts for the WhatsApp message
  useEffect(() => {
    if (!profile?.id) return;
    Promise.all([
  supabase
    .from("profiles")
    .select("id", { count: "exact", head: true })
    .eq("owner_id", profile.id)
    .eq("role", "agent")
    .is("deleted_at", null)
    .eq("is_active", true)          // ← only active
    .eq("plan_suspended", false),   // ← only non-suspended
  supabase
    .from("shops")
    .select("id", { count: "exact", head: true })
    .eq("owner_id", profile.id)
    .is("deleted_at", null)
    .eq("active", true)
    .eq("plan_suspended", false),
]).then(([{ count: a }, { count: s }]) => {
  setAgentCount(a ?? 0);
  setShopCount(s ?? 0);
});
  }, [profile?.id]);

  useEffect(() => {
    if (!paymentSuccess) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [paymentSuccess]);

  // Keep sub-monthly `frequency` in sync with yearly toggle
  useEffect(() => {
    if (isYearly) setFrequency("monthly");
  }, [isYearly]);

   const handleSelectPlan = (plan: Plan) => {
    const elig = planEligibility(plan, agentCount, shopCount);
    if (!elig.eligible) {
      const fit = nextFittingPlan(plans, agentCount, shopCount);
      setBlocked({
        planName: plan.name,
        reason: elig.reason,
        fixPlanId: fit?.id ?? null,
      });
      return;
    }

    setBlocked(null);
    setSelected(plan);
    resetPaymentState();
    resetManualForm();
    setPhone("");
    setPayTab("stk");
    setStep("pay");
    if (!isYearly) setFrequency("monthly");
  };

    const handlePay = async () => {
    if (!phone.trim() || !selected) return;

    const elig = planEligibility(selected, agentCount, shopCount);
    if (!elig.eligible) {
      setBlocked({
        planName: selected.name,
        reason: elig.reason,
        fixPlanId: null,
      });
      return;
    }

    await triggerStkPush(phone.trim(), selected.id, effectiveFrequency);
  };

    const handleManualPay = async () => {
    if (!manualRef.trim() || !selected) return;

    const elig = planEligibility(selected, agentCount, shopCount);
    if (!elig.eligible) {
      setBlocked({
        planName: selected.name,
        reason: elig.reason,
        fixPlanId: null,
      });
      return;
    }
   };
 

  const handleBack = () => {
    resetPaymentState();
    resetManualForm();
    setStep("plans");
  };

    const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      // Fallback if the context method throws
      window.location.href = "/login";
    }
  };

  // ── Theme tokens ─────────────────────────────────────────────
  const T = {
    surface:       theme.bg.card,
    surfaceMuted:  theme.bg.base,
    border:        theme.border.default,
    text:          theme.text.primary,
    textSecondary: theme.text.secondary,
    textMuted:     theme.text.muted,
    fontBody:      theme.font.body,
    fontDisplay:   theme.font.display,
    fontMono:      theme.font.mono,
  };

  const cardStyle: React.CSSProperties = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: isMobile ? 18 : 22,
    width: "100%",
    maxWidth: 460,
  
    // ── Scroll fix ──────────────────────────────────────────
    // Never exceed the (smallest) viewport; scroll internally
    // instead. 100svh is the viewport height *with* mobile
    // browser chrome visible, so this always fits on screen.
    maxHeight: "calc(100svh - 24px)",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    // ────────────────────────────────────────────────────────
  
    animation: "fadeUp 0.3s ease",
    color: T.text,
    fontFamily: T.fontBody,
  };

  // ── Success ───────────────────────────────────────────────────
  if (paymentSuccess) {
    return (
      <div style={cardStyle}>
        <style>{ANIM_CSS}</style>
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            {CONFETTI_COLORS.map((color, i) => (
              <div key={i} style={{
                position: "absolute",
                left: `${(i * 17 + 5) % 100}%`,
                top: "-10px",
                width: i % 3 === 0 ? 8 : 6,
                height: i % 3 === 0 ? 8 : 6,
                borderRadius: i % 2 === 0 ? "50%" : 2,
                background: color,
                animation: `confettiFall ${1.2 + (i % 5) * 0.3}s ease-in ${i * 0.07}s both`,
              }} />
            ))}
          </div>
          <div style={{ textAlign: "center", padding: isMobile ? "36px 20px 32px" : "44px 32px 36px", position: "relative" }}>
            <div style={{ fontSize: 56, marginBottom: 14, animation: "successPop 0.5s cubic-bezier(.175,.885,.32,1.275)" }}>🎊</div>
            <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 24 : 28, marginBottom: 6, color: T.text }}>
              Payment Confirmed!
            </div>
            <div style={{ fontFamily: T.fontMono, fontSize: 13, color: T.textSecondary, lineHeight: 1.8, marginBottom: 8 }}>
              Your{" "}
              <strong style={{ color: MPESA_LIGHT }}>{selected?.name ?? ""}</strong>{" "}
              plan is now active.
            </div>
            <div style={{ fontFamily: T.fontMono, fontSize: 12, color: GOLD, marginBottom: 24 }}>
              Your business is ready — keep winning! 💪
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              style={{ background: `linear-gradient(135deg,${MPESA_GREEN},#15803d)`, border: "none", borderRadius: 14, padding: "13px 32px", color: "#fff", fontFamily: T.fontBody, fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: `0 4px 20px ${MPESA_GREEN}40` }}
            >
              Go to Dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Pending (M-Pesa prompt sent) ──────────────────────────────
  if (paymentPending) {
    return (
      <div style={cardStyle}>
        <style>{ANIM_CSS}</style>
        <div style={{ background: `linear-gradient(135deg,${MPESA_GREEN},#166534)`, padding: "28px 20px 24px", textAlign: "center" }}>
          <div style={{ display: "inline-block", animation: "phoneBounce 0.7s ease infinite alternate" }}>
            <svg width="72" height="120" viewBox="0 0 72 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="2" y="2" width="68" height="116" rx="12" fill="#1a1a2e" stroke="#3a3a5c" strokeWidth="1.5"/>
              <rect x="0" y="30" width="2.5" height="14" rx="1.25" fill="#2d2d4e"/>
              <rect x="0" y="48" width="2.5" height="20" rx="1.25" fill="#2d2d4e"/>
              <rect x="69.5" y="38" width="2.5" height="22" rx="1.25" fill="#2d2d4e"/>
              <rect x="6" y="12" width="60" height="96" rx="6" fill="#0f172a"/>
              <rect x="24" y="10" width="24" height="5" rx="2.5" fill="#1a1a2e"/>
              <circle cx="36" cy="8.5" r="1.5" fill="#2d2d4e"/>
              <rect x="6" y="12" width="60" height="10" rx="0" fill="#0ea5e9" opacity="0.15"/>
              <text x="11" y="20" fontFamily="monospace" fontSize="5" fill="rgba(255,255,255,0.7)">9:41</text>
              <text x="48" y="20" fontFamily="monospace" fontSize="5" fill="rgba(255,255,255,0.7)">▮▮▮</text>
              <rect x="6" y="22" width="60" height="18" fill="#16a34a"/>
              <text x="36" y="34" fontFamily="sans-serif" fontSize="6.5" fontWeight="bold" fill="white" textAnchor="middle">M-PESA</text>
              <rect x="10" y="44" width="52" height="20" rx="4" fill="#1e293b" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
              <text x="36" y="52" fontFamily="monospace" fontSize="5" fill="rgba(255,255,255,0.5)" textAnchor="middle">Amount</text>
              <text x="36" y="61" fontFamily="monospace" fontSize="7.5" fontWeight="bold" fill="#22c55e" textAnchor="middle">KSh {pendingAmount > 0 ? pendingAmount.toLocaleString() : "—"}</text>
              <text x="36" y="74" fontFamily="sans-serif" fontSize="5.5" fill="rgba(255,255,255,0.75)" textAnchor="middle">Enter M-PESA PIN</text>
              <circle cx="24" cy="83" r="3" fill="#334155"/>
              <circle cx="31" cy="83" r="3" fill="#334155"/>
              <circle cx="38" cy="83" r="3" fill="#334155"/>
              <circle cx="45" cy="83" r="3" fill="#334155"/>
              <rect x="10" y="89" width="52" height="11" rx="3" fill="#16a34a" opacity="0.9"/>
              <text x="36" y="97" fontFamily="sans-serif" fontSize="5.5" fontWeight="bold" fill="white" textAnchor="middle">OK</text>
              <rect x="28" y="107" width="16" height="2.5" rx="1.25" fill="rgba(255,255,255,0.2)"/>
            </svg>
          </div>
        </div>
        <div style={{ padding: isMobile ? "24px 20px" : "28px 28px", textAlign: "center" }}>
          <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 20 : 22, color: T.text, marginBottom: 8 }}>
            Check Your Phone!
          </div>
          <div style={{ fontFamily: T.fontMono, fontSize: 12, color: T.textSecondary, lineHeight: 1.8, marginBottom: 16 }}>
            An M-Pesa prompt has been sent to your phone.<br />
            Enter your PIN to confirm{" "}
            <strong style={{ color: GOLD, fontSize: 14 }}>KSh {pendingAmount.toLocaleString()}</strong>.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 10, padding: "10px 16px" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: MPESA_LIGHT, animation: "pulse 1s infinite" }} />
            <span style={{ fontFamily: T.fontMono, fontSize: 11, color: isDark ? "#4ade80" : "#15803d" }}>
              This page unlocks automatically once payment is confirmed
            </span>
          </div>
        </div>
      </div>
    );
  }

    const hasAnyBlockedPlan =
    agentCount !== null &&
    shopCount !== null &&
    plans.some(p => !planEligibility(p, agentCount, shopCount).eligible);


  // ── Plan selection ────────────────────────────────────────────
  if (step === "plans") {
    return (
    <>
        {blocked && (
          <BlockedToast
            data={blocked}
            plans={plans}
            isDark={isDark}
            theme={theme}
            onDismiss={() => setBlocked(null)}
            onManage={openManage}
            onSwitch={(planId) => {
              const target = plans.find(p => p.id === planId);
              setBlocked(null);
              if (target) handleSelectPlan(target);
            }}
          />
        )}
      
      <div style={cardStyle}>
        <style>{ANIM_CSS}</style>

        <TeamManagerModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          agentList={agentList}
          shopList={shopList}
          setAgentList={setAgentList}
          setShopList={setShopList}
          loading={listLoading}
          error={listError}
          setError={setListError}
          theme={theme}
          isDark={isDark}
          isMobile={isMobile}
          onCountsChanged={handleCountsChanged}
        />

                {/* Hero banner — branded gradient stays constant across themes */}
        <div style={{
          background: "linear-gradient(135deg,#052e16,#14532d,#166534)",
          padding: isMobile ? "24px 20px 20px" : "28px 28px 22px",
          borderBottom: "1px solid rgba(34,197,94,0.15)",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                💚
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 18 : 20, color: "#f0fdf4", letterSpacing: "-0.02em" }}>
                  Choose Your Plan
                </div>
                <div style={{ fontFamily: T.fontMono, fontSize: 10, color: "rgba(187,247,208,0.7)", marginTop: 2 }}>
                  Pay once. Sell every day.
                </div>
              </div>
            </div>

            {/* Sign out — top right of the hero */}
            <button
              onClick={handleLogout}
              style={{
                flexShrink: 0,
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: 8,
                padding: "6px 12px",
                color: "rgba(240,253,244,0.92)",
                fontFamily: T.fontMono,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Sign out
            </button>
          </div>

          <div style={{ fontFamily: T.fontMono, fontSize: 11, color: "rgba(187,247,208,0.7)", lineHeight: 1.7 }}>
            Your trial has ended. Pick a plan and keep your team selling.
          </div>
        </div>

        <div style={{ padding: isMobile ? "18px 16px" : "22px 24px" }}>

         <div style={{ padding: isMobile ? "18px 16px" : "22px 24px" }}>
          {/* ── Your usage strip ── */}
          <UsageStrip
            agentCount={agentCount}
            shopCount={shopCount}
            isMobile={isMobile}
            hasAnyBlockedPlan={hasAnyBlockedPlan}
            onManage={openManage}
          />

          {/* ── Yearly toggle ── */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
            padding: "10px 14px",
            background: T.surfaceMuted,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 16 }}>💰</span>
              <div>
                <div style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: 13, color: T.text }}>
                  {isYearly ? "Yearly billing" : "Monthly billing"}
                </div>
                <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, marginTop: 1 }}>
                  {isYearly ? "Get 1 month free" : "Switch to save a month"}
                </div>
              </div>
            </div>
            <label style={{ position: "relative", display: "inline-block", width: 48, height: 26, cursor: "pointer", flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={isYearly}
                onChange={() => setIsYearly(v => !v)}
                style={{ opacity: 0, width: 0, height: 0 }}
                aria-label="Toggle yearly billing"
              />
              <span style={{
                position: "absolute",
                inset: 0,
                backgroundColor: isYearly ? MPESA_GREEN : (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.18)"),
                borderRadius: 99,
                transition: "background 0.2s",
              }}>
                <span style={{
                  position: "absolute",
                  height: 20, width: 20,
                  left: isYearly ? 25 : 3,
                  top: 3,
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  transition: "left 0.2s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }} />
              </span>
            </label>
          </div>

          {/* Plan cards */}
          {plans.length === 0 && <PlansEmpty />}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            {plans.map((plan, idx) => {
              const isHustler  = plan.id === "growth";
              const isCustom   = plan.is_custom;
              const accent     = isHustler ? GOLD : isCustom ? "#c084fc" : MPESA_LIGHT;
              const borderBase = isHustler ? `rgba(245,158,11,0.35)` : isCustom ? "rgba(192,132,252,0.25)" : "rgba(34,197,94,0.25)";
              const bgBase     = isHustler ? "rgba(245,158,11,0.05)" : "rgba(34,197,94,0.04)";
              const elig        = planEligibility(plan, agentCount, shopCount);
              const isBlocked   = !elig.eligible;



              // Price depends on toggle
              const displayPrice = isYearly ? yearlyPrice(plan.price) : plan.price;
              const periodLabel  = isYearly ? "/year" : "/month";
              const savings      = plan.price; // 1 month free = one monthly price saved
              const savePct      = Math.round((savings / yearlyPrice(plan.price)) * 100);

              return (
                <button
                key={plan.id}
                onClick={() => handleSelectPlan(plan)}
                aria-disabled={isBlocked}
                style={{
                  background: bgBase,
                  border: `1.5px solid ${isBlocked ? `${RED}40` : borderBase}`,
                  borderRadius: 14,
                  padding: isMobile ? "14px 14px" : "16px 18px",
                  cursor: isBlocked ? "not-allowed" : "pointer",
                  textAlign: "left",
                  position: "relative",
                  transition: "border-color 0.15s, transform 0.1s",
                  animation: `fadeUp 0.3s ease ${idx * 0.08}s both`,
                  color: T.text,
                  fontFamily: T.fontBody,
                  opacity: isBlocked ? 0.55 : 1,
                  filter: isBlocked ? "grayscale(0.4)" : "none",
                }}
                onMouseEnter={e => {
                  if (isBlocked) return;
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = accent;
                  el.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={e => {
                  if (isBlocked) return;
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = borderBase;
                  el.style.transform = "none";
                }}
                >
                  {isHustler && (
                    <div style={{ position: "absolute", top: -11, left: 16, background: `linear-gradient(135deg,${GOLD},#d97706)`, borderRadius: 20, padding: "2px 12px", fontSize: 10, fontFamily: T.fontMono, color: "#000", fontWeight: 800, whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
                      ⚡ MOST POPULAR
                    </div>
                  )}
                  {isBlocked && (
                  <div style={{
                    position: "absolute",
                    top: -11,
                    right: 16,
                    background: `linear-gradient(135deg,${RED},#b91c1c)`,
                    borderRadius: 20,
                    padding: "2px 12px",
                    fontSize: 10,
                    fontFamily: T.fontMono,
                    color: "#fff",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    letterSpacing: "0.04em",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    zIndex: 2,
                  }}>
                    🔒 DOESN'T FIT
                  </div>
                )}

                {isYearly && !isBlocked && (
                  <div style={{
                    position: "absolute",
                    top: -11,
                    right: 16,
                    background: "linear-gradient(135deg,#16a34a,#15803d)",
                    borderRadius: 20,
                    padding: "2px 12px",
                    fontSize: 10,
                    fontFamily: T.fontMono,
                    color: "#fff",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    letterSpacing: "0.04em",
                  }}>
                    🎁 1 MONTH FREE
                  </div>
                )}

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        <span style={{ fontSize: isHustler ? 18 : 16 }}>{isHustler ? "💸" : isCustom ? "🏢" : "🌱"}</span>
                        <span style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 15 : 17, color: accent }}>
                          {plan.name}
                        </span>
                        
                      </div>
                      <PlanLimits plan={plan} />
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 18 : 21, color: accent, whiteSpace: "nowrap" }}>
                        KSh {displayPrice.toLocaleString()}
                      </div>
                      <div style={{ fontFamily: T.fontMono, fontSize: 9, color: T.textMuted }}>{periodLabel}</div>
                      {isYearly ? (
                        <div style={{ fontSize: 9, fontFamily: T.fontMono, color: MPESA_GREEN, marginTop: 4, fontWeight: 700 }}>
                          save KSh {savings.toLocaleString()} ({savePct}%)
                        </div>
                      ) : (
                        (plan.price_daily != null || plan.price_weekly != null) && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6, alignItems: "flex-end" }}>
                            {plan.price_daily != null && (
                              <span style={{ fontSize: 9, fontFamily: T.fontMono, color: accent, background: `${accent}15`, border: `1px solid ${accent}30`, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>
                                KSh {plan.price_daily.toLocaleString()}/day
                              </span>
                            )}
                            {plan.price_weekly != null && (
                              <span style={{ fontSize: 9, fontFamily: T.fontMono, color: accent, background: `${accent}15`, border: `1px solid ${accent}30`, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>
                                KSh {plan.price_weekly.toLocaleString()}/week
                              </span>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                    {FEATURE_LABELS.map(({ key, label }) => {
                      const on = isCustom || plan.features[key] !== false;
                      return (
                        <span key={key} style={{ fontSize: 11, fontFamily: T.fontMono, color: on ? MPESA_GREEN : T.textMuted }}>
                          {on ? "✓" : "✕"} {label}
                        </span>
                      );
                    })}
                    <span style={{ fontSize: 11, fontFamily: T.fontMono, color: MPESA_GREEN }}>
                      ✓ Customer Messaging
                    </span>
                   </div>


                                    <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {isBlocked ? (
                      <>
                        <span style={{ fontFamily: T.fontMono, fontSize: 10, color: RED, flex: 1, lineHeight: 1.5 }}>
                          {elig.reason} Tap to see options.
                        </span>
                        <span style={{
                          fontFamily: T.fontMono, fontSize: 11, color: RED,
                          background: `${RED}15`, border: `1px solid ${RED}40`,
                          borderRadius: 20, padding: "3px 12px", whiteSpace: "nowrap",
                        }}>
                          Upgrade needed
                        </span>
                      </>
                    
                    ) : (
                      <span style={{
                        fontFamily: T.fontMono, fontSize: 11, color: accent,
                        background: `${accent}15`, border: `1px solid ${accent}30`,
                        borderRadius: 20, padding: "3px 12px",
                      }}>
                        Select →
                      </span>
                    )}
                      </div>
                                  </button>
                                );
                              })}
                            </div>

          {/* Custom plan */}
          <div style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.22)", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 16 }}>🏢</span>
                <span style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#c084fc" }}>Custom Plan</span>
              </div>
              <div style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: 12, color: "#c084fc", background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: 8, padding: "3px 10px", whiteSpace: "nowrap" }}>
                Contact Us
              </div>
            </div>
            <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
              Running a larger operation? Get a plan tailored to your business.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {[
                { icon: "📞", text: "0768 131 905", href: "tel:0768131905" },
                { icon: "📞", text: "0791 408 944", href: "tel:0791408944" },
                { icon: "✉",  text: "epicsoftwaredesigners@gmail.com", href: "mailto:epicsoftwaredesigners@gmail.com" },
              ].map(l => (
                <a key={l.href} href={l.href} style={{ display: "flex", alignItems: "center", gap: 8, color: T.textSecondary, fontSize: 12, fontFamily: T.fontMono, textDecoration: "none" }}>
                  <span>{l.icon}</span> {l.text}
                </a>
              ))}
            </div>

            <a
              href={buildWaHref(agentCount, shopCount)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: 8,
                marginTop: 12,
                background: "rgba(37,211,102,0.1)",
                border: "1px solid rgba(37,211,102,0.3)",
                borderRadius: 10,
                padding: "9px 14px",
                textDecoration: "none",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "rgba(37,211,102,0.18)")}
              onMouseLeave={e => (e.currentTarget.style.background = "rgba(37,211,102,0.1)")}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="#25D366" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: 12, color: "#25D366" }}>
                  Chat us on WhatsApp
                </div>
                <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, marginTop: 1 }}>
                  Click me — we'll explain how it works
                </div>
              </div>
              <span style={{ fontSize: 12, color: "#25D366" }}>→</span>
            </a>
          </div>

          {/* Trust strip */}
          <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 12 : 20, flexWrap: "wrap" }}>
            {[
              { icon: "🔒", text: "Secure payment" },
              { icon: "📱", text: "M-Pesa only" },
              { icon: "✓",  text: "Cancel anytime" },
            ].map(t => (
              <div key={t.text} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: T.fontMono, fontSize: 10, color: T.textMuted }}>
                <span style={{ fontSize: 12 }}>{t.icon}</span> {t.text}
              </div>
            ))}
          </div>
          </div>

          </div>
      </div>
      </>
    );
  }

  // ── Pay step ──────────────────────────────────────────────────
  const isHustlerSelected = selected?.id === "growth";
  const payAccent = isHustlerSelected ? GOLD : MPESA_GREEN;

  const resolvedPrice = selected
    ? isYearly
      ? yearlyPrice(selected.price)
      : frequency === "daily"  && (selected as any).price_daily  != null ? (selected as any).price_daily  as number
      : frequency === "weekly" && (selected as any).price_weekly != null ? (selected as any).price_weekly as number
      : selected.price
    : 0;

  const durationLabel = isYearly ? "/year"
    : frequency === "daily"  && (selected as any)?.price_daily  != null ? "/day"
    : frequency === "weekly" && (selected as any)?.price_weekly != null ? "/week"
    : "/month";

  const hasSubMonthly = selected
    ? (selected as any).price_daily != null || (selected as any).price_weekly != null
    : false;

  const yearlySavings = selected ? selected.price : 0;

    return (
    <>
      {blocked && (
                  <BlockedToast
            data={blocked}
            plans={plans}
            isDark={isDark}
            theme={theme}
            onDismiss={() => setBlocked(null)}
            onManage={openManage}
            onSwitch={(planId) => {
              const target = plans.find(p => p.id === planId);
              setBlocked(null);
              if (target) handleSelectPlan(target);
            }}
          />
      )}

      <div style={cardStyle}>
        <style>{ANIM_CSS}</style>

        <TeamManagerModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          agentList={agentList}
          shopList={shopList}
          setAgentList={setAgentList}
          setShopList={setShopList}
          loading={listLoading}
          error={listError}
          setError={setListError}
          theme={theme}
          isDark={isDark}
          isMobile={isMobile}
          onCountsChanged={handleCountsChanged}
        />

        {/* M-Pesa branded top bar */}
      <div style={{
        background: `linear-gradient(135deg,${isHustlerSelected ? "#422006" : "#052e16"},${isHustlerSelected ? "#78350f" : "#14532d"})`,
        padding: isMobile ? "18px 16px" : "22px 24px",
        borderBottom: `1px solid ${payAccent}25`,
      }}>
        <button onClick={handleBack} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8, padding: "5px 11px", color: "rgba(255,255,255,0.75)", fontFamily: T.fontMono, fontSize: 11, cursor: "pointer", marginBottom: 14 }}>
          ← Back
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 18 }}>{isHustlerSelected ? "💸" : "🌱"}</span>
              <span style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 17 : 19, color: "#f0fdf4" }}>
                {selected?.name}
              </span>
              {isYearly && (
                <span style={{ fontSize: 9, fontFamily: T.fontMono, color: "#f0fdf4", background: "rgba(255,255,255,0.18)", borderRadius: 4, padding: "2px 7px", fontWeight: 700 }}>
                  YEARLY
                </span>
              )}
            </div>
            {selected && <PlanLimits plan={selected} />}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: isMobile ? 22 : 26, color: payAccent }}>
              KSh {resolvedPrice.toLocaleString()}
            </div>
            <div style={{ fontFamily: T.fontMono, fontSize: 10, color: "rgba(255,255,255,0.55)" }}>{durationLabel}</div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: isMobile ? "20px 16px" : "24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Yearly savings banner on the pay step */}
        {isYearly && selected && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.25)",
            borderRadius: 10,
            padding: "10px 14px",
          }}>
            <span style={{ fontSize: 16 }}>🎁</span>
            <div style={{ flex: 1, fontFamily: T.fontMono, fontSize: 12, color: MPESA_GREEN, lineHeight: 1.5 }}>
              You're saving <strong>KSh {yearlySavings.toLocaleString()}</strong> — that's 1 month free vs paying monthly.
            </div>
          </div>
        )}

        {/* Frequency selector — hidden when yearly */}
        {hasSubMonthly && !isYearly && (
          <div>
            <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8 }}>
              Payment Frequency
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {([
                (selected as any).price_daily  != null ? { key: "daily",   label: "Daily",   price: (selected as any).price_daily  as number } : null,
                (selected as any).price_weekly != null ? { key: "weekly",  label: "Weekly",  price: (selected as any).price_weekly as number } : null,
                { key: "monthly", label: "Monthly", price: selected!.price },
              ].filter(Boolean) as { key: "daily" | "weekly" | "monthly"; label: string; price: number }[]).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFrequency(opt.key)}
                  style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    border: `1.5px solid ${frequency === opt.key ? payAccent : T.border}`,
                    borderRadius: 10, padding: "9px 6px", cursor: "pointer",
                    background: frequency === opt.key ? `${payAccent}18` : T.surfaceMuted,
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: frequency === opt.key ? payAccent : T.textSecondary, fontFamily: T.fontBody }}>{opt.label}</div>
                  <div style={{ fontSize: 11, fontFamily: T.fontMono, color: frequency === opt.key ? payAccent : T.textMuted, marginTop: 2 }}>KSh {opt.price.toLocaleString()}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Payment method tabs */}
        <div style={{ display: "flex", gap: 8 }}>
          {([
            { id: "stk",    label: "📱 M-Pesa STK",       hint: "Auto-prompt" },
            { id: "manual", label: "💳 Pay via Reference", hint: "Any method"  },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => { setPayTab(tab.id); resetManualForm(); resetPaymentState?.(); }}
              style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                border: `1.5px solid ${payTab === tab.id ? payAccent : T.border}`,
                borderRadius: 12, padding: "10px 8px", cursor: "pointer",
                background: payTab === tab.id ? `${payAccent}15` : T.surfaceMuted,
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: 12, color: payTab === tab.id ? payAccent : T.textSecondary }}>{tab.label}</span>
              <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted }}>{tab.hint}</span>
            </button>
          ))}
        </div>

        {/* ── STK tab ── */}
        {payTab === "stk" && (
          <>
            <div>
              <label style={{ display: "block", fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                Safaricom M-Pesa Number
              </label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontFamily: T.fontMono, fontSize: 13, color: T.textMuted }}>
                  📱
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handlePay(); }}
                  placeholder="07XX XXX XXX"
                  autoFocus
                  style={{ width: "100%", background: T.surfaceMuted, border: `1px solid ${T.border}`, borderRadius: 12, padding: "13px 16px 13px 42px", color: T.text, fontSize: 16, fontFamily: T.fontBody, boxSizing: "border-box", outline: "none" }}
                />
              </div>
              <div style={{ marginTop: 6, fontFamily: T.fontMono, fontSize: 10, color: T.textMuted }}>
                Safaricom numbers only — you'll get a PIN prompt on your phone
              </div>
            </div>

            {paymentError && (
              <div style={{ color: "#f87171", fontSize: 12, fontFamily: T.fontMono, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "10px 14px" }}>
                ⚠ {paymentError}
              </div>
            )}

            <button
              onClick={handlePay}
              disabled={!phone.trim()}
              style={{
                width: "100%",
                background: phone.trim() ? `linear-gradient(135deg,${payAccent},${isHustlerSelected ? "#d97706" : "#15803d"})` : T.surfaceMuted,
                border: "none", borderRadius: 14, padding: "15px 0",
                color: phone.trim() ? "#fff" : T.textMuted,
                fontFamily: T.fontBody, fontWeight: 800, fontSize: 16,
                cursor: phone.trim() ? "pointer" : "not-allowed",
                boxShadow: phone.trim() ? `0 4px 20px ${payAccent}40` : "none",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              } as React.CSSProperties}
            >
              <span style={{ fontSize: 18 }}>💚</span>
              Pay KSh {resolvedPrice.toLocaleString()} via M-Pesa →
            </button>

            <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textAlign: "center" }}>
              🔒 Processed securely through Safaricom Daraja
            </div>
          </>
        )}

        {/* ── Manual reference tab ── */}
        {payTab === "manual" && (
          manualSuccess ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
              <div style={{ fontFamily: T.fontDisplay, fontWeight: 800, fontSize: 18, color: T.text, marginBottom: 8 }}>Reference Received!</div>
              <div style={{ fontFamily: T.fontMono, fontSize: 12, color: T.textSecondary, lineHeight: 1.8 }}>
                Our team will verify your payment and activate your plan.<br />
                This usually takes <strong style={{ color: GOLD }}>a few hours</strong>. You can close this and check back later.
              </div>
            </div>
          ) : (
            <>
              <div style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.22)", borderRadius: 12, padding: "14px 18px" }}>
                <div style={{ fontSize: 10, fontFamily: T.fontMono, color: T.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8 }}>
                  Pay to our Till Number
                </div>
                <div style={{ display: "flex", gap: 28, flexWrap: "wrap" as const }}>
                  <div>
                    <div style={{ fontSize: 10, fontFamily: T.fontMono, color: T.textMuted }}>Till Number</div>
                    <div style={{ fontFamily: T.fontMono, fontWeight: 700, fontSize: 24, color: T.text, letterSpacing: "0.12em" }}>{TILL_NUMBER}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontFamily: T.fontMono, color: T.textMuted }}>Amount</div>
                    <div style={{ fontFamily: T.fontMono, fontWeight: 700, fontSize: 24, color: GOLD }}>KSh {resolvedPrice.toLocaleString()}</div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: 11, fontFamily: T.fontMono, color: T.textSecondary, lineHeight: 1.7 }}>
                  Pay via <strong style={{ color: T.text }}>M-Pesa</strong>, <strong style={{ color: T.text }}>Airtel Money</strong>, any <strong style={{ color: T.text }}>bank app</strong>, or walk to an M-Pesa agent.<br />
                  Then paste your confirmation code below.
                </div>
              </div>

              <div>
                <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8 }}>
                  Payment Method
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                  {([
                    { value: "mpesa_manual", label: "M-Pesa" },
                    { value: "airtel",       label: "Airtel Money" },
                    { value: "bank",         label: "Bank App" },
                    { value: "other",        label: "Other" },
                  ] as const).map(m => (
                    <button
                      key={m.value}
                      onClick={() => setManualMethod(m.value)}
                      style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${manualMethod === m.value ? payAccent : T.border}`, background: manualMethod === m.value ? `${payAccent}15` : T.surfaceMuted, color: manualMethod === m.value ? payAccent : T.textSecondary, fontFamily: T.fontMono, fontSize: 11, cursor: "pointer", transition: "all 0.15s" }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8 }}>
                  Transaction Reference *
                </label>
                <input
                  type="text"
                  value={manualRef}
                  onChange={e => { setManualRef(e.target.value.toUpperCase().replace(/[^A-Z0-9\-\/]/g, "").slice(0, 30)); setManualError(""); }}
                  placeholder={manualMethod === "mpesa_manual" ? "e.g. QEK7YHM2S3" : manualMethod === "airtel" ? "Airtel confirmation code" : "Bank reference / transaction ID"}
                  style={{ width: "100%", boxSizing: "border-box", background: T.surfaceMuted, border: `1.5px solid ${manualRef.length >= 8 ? payAccent : T.border}`, borderRadius: 11, padding: "13px 16px", color: T.text, fontSize: 15, fontFamily: T.fontMono, letterSpacing: "0.08em", outline: "none", transition: "border-color 0.18s" }}
                />
                {manualMethod === "mpesa_manual" && (
                  <div style={{ marginTop: 6, fontSize: 11, fontFamily: T.fontMono, color: manualRef.length >= 8 && manualRef.length <= 12 ? MPESA_GREEN : T.textMuted }}>
                    {manualRef.length >= 8 && manualRef.length <= 12 ? "✓ Looks like a valid M-Pesa code" : "M-Pesa receipt from your SMS — e.g. QEK7YHM2S3"}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: "block", fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 8 }}>
                  Phone Used (optional)
                </label>
                <input
                  type="tel"
                  value={manualPhone}
                  onChange={e => setManualPhone(e.target.value.replace(/[^\d\s\-+()]/g, "").slice(0, 15))}
                  placeholder="07XXXXXXXX"
                  style={{ width: "100%", boxSizing: "border-box", background: T.surfaceMuted, border: `1px solid ${T.border}`, borderRadius: 11, padding: "12px 16px", color: T.text, fontSize: 14, fontFamily: T.fontBody, outline: "none" }}
                />
              </div>

              {manualError && (
                <div style={{ color: "#f87171", fontSize: 12, fontFamily: T.fontMono, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "10px 14px" }}>
                  ⚠ {manualError}
                </div>
              )}

              <button
                onClick={handleManualPay}
                disabled={!manualRef.trim()}
                style={{
                  width: "100%", border: "none", borderRadius: 14, padding: "15px 0",
                  background: manualRef.trim() ? `linear-gradient(135deg,#6366f1,#4f46e5)` : T.surfaceMuted,
                  color: manualRef.trim() ? "#fff" : T.textMuted,
                  fontFamily: T.fontBody, fontWeight: 800, fontSize: 16,
                  cursor: manualRef.trim() ? "pointer" : "not-allowed",
                  boxShadow: manualRef.trim() ? "0 4px 20px rgba(99,102,241,0.4)" : "none",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                } as React.CSSProperties}
              >
                  Submit Payment Reference →
              </button>

              <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, textAlign: "center" }}>
                Our team verifies and activates your plan — usually within a few hours
              </div>
            </>
          )
        )}

         </div>
      </div>
    </>
  );
}

// ── Plans loading/empty fallback (theme-aware) ──────────────
function PlansEmpty() {
  const { theme } = useTheme();
  const [waited, setWaited] = useState(false);
  useEffect(() => { const t = setTimeout(() => setWaited(true), 3000); return () => clearTimeout(t); }, []);

  if (!waited) {
    return (
      <div style={{ textAlign: "center", padding: "24px 0", color: theme.text.muted, fontFamily: theme.font.mono, fontSize: 12 }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${theme.border.default}`, borderTopColor: MPESA_GREEN, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
        Loading plans…
      </div>
    );
  }
  return (
    <div style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "14px 16px", marginBottom: 16, fontFamily: theme.font.mono, fontSize: 12, color: "#f87171", lineHeight: 1.7 }}>
      ⚠ No plans found in the database.<br />
      <span style={{ color: theme.text.muted }}>Add plans in Supabase then refresh the page.</span>
    </div>
  );
}

const CONFETTI_COLORS = [
  "#16a34a","#22c55e","#f59e0b","#fbbf24","#06b6d4","#38bdf8",
  "#a855f7","#ec4899","#ef4444","#f97316","#84cc16","#14b8a6",
];

const ANIM_CSS = `
  @keyframes fadeUp      { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin        { to{transform:rotate(360deg)} }
  @keyframes pulse       { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes successPop  { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.18)} 100%{transform:scale(1);opacity:1} }
  @keyframes phoneBounce { from{transform:translateY(0) rotate(-4deg)} to{transform:translateY(-8px) rotate(4deg)} }
  @keyframes confettiFall{ from{opacity:1;transform:translateY(0) rotate(0deg)} to{opacity:0;transform:translateY(180px) rotate(360deg)} }
  @keyframes toastIn { from{opacity:0;transform:translateY(-10px) scale(0.98)} to{opacity:1;transform:translateY(0) scale(1)} }
`;